import React from 'react';
import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
} from '@mui/material';
import { Chat as ChatIcon } from '@mui/icons-material';

/**
 * Highlights occurrences of searchTerm within text using <mark>.
 * Escapes regex special chars to prevent crashes.
 */
const HighlightedText = ({ text, searchTerm }) => {
  if (!text || !searchTerm) return text || null;

  const escaped = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));

  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <Box
            key={i}
            component="span"
            sx={{ bgcolor: 'action.selected', fontWeight: 600, borderRadius: 0.5, px: 0.25 }}
          >
            {part}
          </Box>
        ) : (
          part
        )
      )}
    </>
  );
};

/**
 * Format relative date string for search results.
 */
const formatRelativeDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Hoy';
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `${diffDays}d`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w`;
  return date.toLocaleDateString();
};

/**
 * SearchResultItem — Single search result with title, snippet, and date.
 *
 * @param {{ thread: object, snippet: string|null, matchType: string, searchTerm: string, onClick: Function }}
 */
const SearchResultItem = ({ thread, snippet, matchType, searchTerm, onClick }) => {
  return (
    <ListItemButton
      onClick={onClick}
      data-testid="search-result-item"
      sx={{
        borderRadius: 1,
        mx: 0.5,
        mb: 0.25,
        py: 0.75,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <ListItemIcon sx={{ minWidth: 36 }}>
        <ChatIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 500, flex: 1, mr: 1 }}>
              <HighlightedText text={thread.title} searchTerm={searchTerm} />
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', flexShrink: 0 }}>
              {formatRelativeDate(thread.updatedAt || thread.createdAt)}
            </Typography>
          </Box>
        }
        secondary={
          snippet ? (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}
              noWrap
            >
              <HighlightedText text={snippet} searchTerm={searchTerm} />
            </Typography>
          ) : null
        }
        disableTypography
      />
    </ListItemButton>
  );
};

export default SearchResultItem;
